/** @format */

import {ChangeEvent, useEffect, useState} from 'react';

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  Hidden,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import * as R from 'ramda';
import {Trans, useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import {v4 as uuidv4} from 'uuid';

import BackHeader from 'components/BackHeader';
import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';
import SecondarySmallButton from 'components/buttons/SecondarySmallButton';
import MediumTextField from 'components/inputs/MediumTextField';
import PrimaryMenu from 'components/menus/PrimaryMenu';
import Modal from 'components/Modal';
import Snackbar from 'components/Snackbar';
import NativeTabs from 'components/tabs/NativeTabs';
import Title from 'components/Title';
import {useCompanyContext} from 'contexts/CompanyContext';
import {useMixpanelContext} from 'contexts/MixpanelContext';
import {fieldName} from 'helpers/field_names';
import {useCallbackOnReturn, useSearchParams} from 'helpers/routerHelpers';
import removeImage from 'images/remove_white.png';
import {Operator} from 'types';

import {YalperAnalyticAttributionModelEnum, YalperPlatform} from 'graphql/generated/types';

import {platformsFromAdType} from 'helpers/adTypeHelpers';
import {ATTRIBUTION_MODELS} from 'pages/Home/Project/Steps/Periods/components/KpiPlanForm/constants';

import {CpmPlanEditor, CpmPlanItem} from './components/CpmPlanEditor';
import MinVisitsFIeld from './components/MinVisitsField';
import {TMinVisits} from './components/MinVisitsField/interfaces';
import {StratLowConvSettings} from './components/StratLowConvSettings';
import {ExceptionForm} from './ExceptionForm';
import {
  FILTERS_TAB,
  KPI_SENSORS,
  MAX_BAD_WORDS,
  MAX_STOP_WORDS,
  NEW_ANOMALY_FALL_SENSORS,
  SENSORS_WITH_SETTINGS,
  SETTINGS_TAB,
  TABS,
  UTMS,
} from './constants';
import {Root, classes} from './styles';
import {
  useCreateExceptionMutation,
  useDeleteExceptionMutation,
  useGetCurrentUserQuery,
  useSaveAdDoubleSettingsMutation,
  useSaveAsKpiAgeGenderSettingsMutation,
  useSaveAsKpiGeoSettingsMutation,
  useSaveAsKpiPlaceSettingsMutation,
  useSaveAsAdAnomalyFallPageDepthSettingsMutation,
  useSaveAsAdAnomalyFallVisitDurationSettingsMutation,
  useSaveAsCampaignAnomalyFallVtrSettingsMutation,
  useSaveAsCpmPlanExceededSettingsMutation,
  useSaveAsMediaAdAnomalyGrowCpmSettingsMutation,
  useSaveGeoGroupsMutation,
  useSaveUtmCoherencySettingsMutation,
  useSaveUtmFormatAppsflyerWebdavSettingsMutation,
  useSaveUtmFormatMetrikaSettingsMutation,
} from './graphql/__generated__';
import {ExceptionFieldsetType, ExceptionType, GeoGroupType} from './types';

export const ErrorSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = useSearchParams();
  const sensorName = searchParams.get('name');
  const humanName = searchParams.get('humanName');

  const hasSettings = () => {
    if (!sensorName) return false;

    return SENSORS_WITH_SETTINGS.includes(sensorName);
  };

  const {t} = useTranslation();

  const [isSnackbarOpen, setIsSnackbarOpen] = useState<boolean>(false);

  const [showAllBadWords, setShowAllBadWords] = useState(false);
  const [showAllStopWords, setShowAllStopWords] = useState(false);
  const [activeTab, setActiveTab] = useState(hasSettings() ? SETTINGS_TAB : FILTERS_TAB);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedField, setSelectedField] = useState('');
  const [isMobileDouble, setIsModineDouble] = useState(false);
  const [skipParamsUtmCoherency, setSkipParamsUtmCoherency] = useState<string[]>([]);
  const [utmFormatValues, setUtmFormatValues] = useState<string[]>(['']);
  const [utmFormatMinVisits, setUtmFormatMinVisits] = useState(0);
  const [utmFormatMinVisitsForOldErrors, setUtmFormatMinVisitsForOldErrors] = useState<number>(0);
  const [kpiPeriodDays, setKpiPeriodDays] = useState<number>(0);
  const [stratLowConvState, setStratLowConvState] = useState({
    periodDays: 7,
    minConversions: 1,
  });
  const [mediaAdAnomalyCpmSettings, setMediaAdAnomalyCpmSettings] = useState({
    periodDays: 0,
    comparisonMultiplierPercent: 130,
    minAnalysisImpressions: 0,
    minComparisonImpressions: 0,
  });
  const [cpmPlanExceededSettings, setCpmPlanExceededSettings] = useState({
    periodDays: 0,
    planCpm: [] as CpmPlanItem[],
    minImpressions: 0,
  });
  const [campaignFallVtrSettings, setCampaignFallVtrSettings] = useState({
    periodDays: 0,
    comparisonMultiplierPercent: 50,
  });
  const [adFallVisitDurationSettings, setAdFallVisitDurationSettings] = useState({
    periodDays: 0,
    comparisonMultiplierPercent: 50,
    attributionModel: 'LAST' as YalperAnalyticAttributionModelEnum,
  });
  const [adFallPageDepthSettings, setAdFallPageDepthSettings] = useState({
    periodDays: 0,
    comparisonMultiplierPercent: 50,
    attributionModel: 'LAST' as YalperAnalyticAttributionModelEnum,
    minVisits: 0,
  });

  const {currentAdType} = useCompanyContext();
  const {mixpanel} = useMixpanelContext();

  const platforms = platformsFromAdType(currentAdType);

  const {loading, error, data, refetch} = useGetCurrentUserQuery({
    skip: !sensorName,
    variables: {sensorName: sensorName!, platforms: platforms},
  });
  const [saveGeoGroups, {loading: saveGeoGroupsLoading}] = useSaveGeoGroupsMutation();
  const [createException] = useCreateExceptionMutation();
  const [deleteException] = useDeleteExceptionMutation();
  const [saveAdDoubleSettings, {loading: saveAdDoubleSettingsLoading}] =
    useSaveAdDoubleSettingsMutation();
  const [saveUtmCoherencySettings, {loading: saveUtmCoherencySettingsLoading}] =
    useSaveUtmCoherencySettingsMutation();
  const [saveUtmFormatSettings, {loading: saveUtmFormatSettingsLoading}] =
    useSaveUtmFormatMetrikaSettingsMutation();
  const [saveUtmFormatWebdavSettings, {loading: saveUtmFormatWebdavSettingsLoading}] =
    useSaveUtmFormatAppsflyerWebdavSettingsMutation();
  const [saveKpiAgeGenderSettings, {loading: saveKpiAgeGenderSettingsLoading}] =
    useSaveAsKpiAgeGenderSettingsMutation();
  const [saveKpiPlaceSettings, {loading: saveKpiPlaceSettingsLoading}] =
    useSaveAsKpiPlaceSettingsMutation();
  const [saveKpiGeoSettings, {loading: saveKpiGeoSettingsLoading}] =
    useSaveAsKpiGeoSettingsMutation();
  const [saveMediaAdAnomalyCpmSettings, {loading: saveMediaAdAnomalyCpmSettingsLoading}] =
    useSaveAsMediaAdAnomalyGrowCpmSettingsMutation();
  const [saveCpmPlanExceededSettings, {loading: saveCpmPlanExceededSettingsLoading}] =
    useSaveAsCpmPlanExceededSettingsMutation();
  const [saveCampaignFallVtrSettings, {loading: saveCampaignFallVtrLoading}] =
    useSaveAsCampaignAnomalyFallVtrSettingsMutation();
  const [saveAdFallVisitDurationSettings, {loading: saveAdFallVisitDurationLoading}] =
    useSaveAsAdAnomalyFallVisitDurationSettingsMutation();
  const [saveAdFallPageDepthSettings, {loading: saveAdFallPageDepthLoading}] =
    useSaveAsAdAnomalyFallPageDepthSettingsMutation();

  useEffect(() => {
    if (!data?.currentUser?.currentCompany) {
      return;
    }

    const {
      adDoubleSettings,
      utmCoherencySettings,
      utmFormatMetrikaSettings,
      utmFormatAppsflyerWebdavSettings,
      asKpiAgeGenderSettings,
      asKpiPlaceSettings,
      asKpiGeoSettings,
      stratLowConvSettings,
      asMediaAdAnomalyGrowCpmSettings,
      asCpmPlanExceededSettings,
      asCampaignAnomalyFallVtrSettings,
      asAdAnomalyFallVisitDurationSettings,
      asAdAnomalyFallPageDepthSettings,
    } = data.currentUser.currentCompany;

    setIsModineDouble(adDoubleSettings.isMobileDouble || false);
    setSkipParamsUtmCoherency(utmCoherencySettings.skipParams as string[]);

    if (sensorName === 'as-kpi-age-gender') {
      setKpiPeriodDays(asKpiAgeGenderSettings.periodDays ?? 0);
    } else if (sensorName === 'as-kpi-place') {
      setKpiPeriodDays(asKpiPlaceSettings.periodDays ?? 0);
    } else if (sensorName === 'as-kpi-geo') {
      setKpiPeriodDays(asKpiGeoSettings.periodDays ?? 0);
    }

    if (sensorName === 'as-strat-low-conv') {
      setStratLowConvState({
        periodDays: stratLowConvSettings.periodDays ?? 7,
        minConversions: stratLowConvSettings.minConversions ?? 1,
      });
    }

    if (sensorName === 'as-utm-format') {
      setUtmFormatMinVisits(utmFormatMetrikaSettings.minVisits);
      setUtmFormatMinVisitsForOldErrors(utmFormatMetrikaSettings.minVisitsForOldProblems);
      const filterSources = utmFormatMetrikaSettings.filterSources as string[];
      setUtmFormatValues(filterSources.length ? filterSources : ['']);
    } else if (sensorName === 'as-utm-format-appsflyer') {
      const filterSources = utmFormatAppsflyerWebdavSettings.filterSources as string[];
      setUtmFormatValues(filterSources.length ? filterSources : ['']);
    }

    if (sensorName === 'as-media-ad-anomaly-grow-cpm') {
      setMediaAdAnomalyCpmSettings({
        periodDays: asMediaAdAnomalyGrowCpmSettings?.periodDays ?? 0,
        comparisonMultiplierPercent:
          asMediaAdAnomalyGrowCpmSettings?.comparisonMultiplierPercent ?? 130,
        minAnalysisImpressions: asMediaAdAnomalyGrowCpmSettings?.minAnalysisImpressions ?? 0,
        minComparisonImpressions: asMediaAdAnomalyGrowCpmSettings?.minComparisonImpressions ?? 0,
      });
    }

    if (sensorName === 'as-cpm-plan-exceeded') {
      setCpmPlanExceededSettings({
        periodDays: asCpmPlanExceededSettings?.periodDays ?? 0,
        planCpm: (asCpmPlanExceededSettings?.planCpm ?? []).map(item => ({
          id: uuidv4(),
          campaignId: item.campaignId,
          cpm: item.cpm,
        })),
        minImpressions: asCpmPlanExceededSettings?.minImpressions ?? 0,
      });
    }

    if (sensorName === 'as-campaign-anomaly-fall-vtr') {
      setCampaignFallVtrSettings({
        periodDays: asCampaignAnomalyFallVtrSettings?.periodDays ?? 0,
        comparisonMultiplierPercent:
          asCampaignAnomalyFallVtrSettings?.comparisonMultiplierPercent ?? 50,
      });
    }

    if (sensorName === 'as-ad-anomaly-fall-visit-duration') {
      setAdFallVisitDurationSettings({
        periodDays: asAdAnomalyFallVisitDurationSettings?.periodDays ?? 0,
        comparisonMultiplierPercent:
          asAdAnomalyFallVisitDurationSettings?.comparisonMultiplierPercent ?? 50,
        attributionModel: asAdAnomalyFallVisitDurationSettings?.attributionModel ?? 'LAST',
      });
    }

    if (sensorName === 'as-ad-anomaly-fall-page-depth') {
      setAdFallPageDepthSettings({
        periodDays: asAdAnomalyFallPageDepthSettings?.periodDays ?? 0,
        comparisonMultiplierPercent:
          asAdAnomalyFallPageDepthSettings?.comparisonMultiplierPercent ?? 50,
        attributionModel: asAdAnomalyFallPageDepthSettings?.attributionModel ?? 'LAST',
        minVisits: asAdAnomalyFallPageDepthSettings?.minVisits ?? 0,
      });
    }
  }, [data, sensorName]);

  useCallbackOnReturn(refetch);

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (!sensorName) {
    return <Alert severity='error'>Не указан сенсор</Alert>;
  }
  if (loading || !data?.currentUser?.currentCompany || saveGeoGroupsLoading) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const {
    id: companyId,
    isClientAccess,
    regions,
    badWords,
    correctWords,
    geoGroups,
    stopWords,
    exceptions,
    exceptionFieldsets,
  } = data.currentUser.currentCompany;

  const handleSnackbarClose = () => {
    setIsSnackbarOpen(false);
  };

  const renderRegions = () => {
    if (sensorName !== 'as-geo') return null;

    const regionNames = regions.map(v => v.name).join(', ');

    const handleClick = () => {
      navigate('/home/main/regions', {
        state: {background: location},
      });
    };

    return (
      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          mt: 4,
        }}
      >
        {regionNames && (
          <Box
            sx={{
              mb: 1,
            }}
          >
            <Typography
              className={classes.sensorSettingsText}
              sx={{
                display: 'inline',
              }}
            >
              <Trans>Ваши регионы: {{regionNames}}</Trans>
            </Typography>
          </Box>
        )}
        <SecondarySmallButton onClick={handleClick}>
          <Typography className={classes.sensorSettingsText}>
            <Trans>Настроить Гео</Trans>
          </Typography>
        </SecondarySmallButton>
      </Box>
    );
  };

  const renderBadWords = () => {
    if (sensorName !== 'as-bad-keys') return null;

    const sortedBadWords = showAllBadWords
      ? badWords.toSorted()
      : badWords.toSorted().slice(0, MAX_BAD_WORDS);

    const moreCount = badWords.length - MAX_BAD_WORDS;
    const showMoreLink = !showAllBadWords && moreCount > 0;
    const showLessLink = showAllBadWords && moreCount > MAX_BAD_WORDS;

    const handleShowMore = () => setShowAllBadWords(true);
    const handleShowLess = () => setShowAllBadWords(false);

    const handleConfigure = () => {
      navigate('/home/main/bad-words', {
        state: {background: location},
      });
    };

    const badWordsText = sortedBadWords.join(', ');
    return (
      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          mt: 4,
        }}
      >
        {sortedBadWords.length > 0 && (
          <Box
            sx={{
              mb: 1,
            }}
          >
            <Typography
              className={classes.sensorSettingsText}
              sx={{
                display: 'inline',
              }}
            >
              <Trans>Ваш чёрный список слов: {{badWordsText}}</Trans>
            </Typography>

            {showMoreLink && (
              <Link className={classes.moreLessLink} component='button' onClick={handleShowMore}>
                <Box
                  sx={{
                    ml: 2,
                  }}
                >
                  <Typography>
                    <Trans>Ещё {{moreCount}}</Trans>
                  </Typography>
                </Box>
              </Link>
            )}

            {showLessLink && (
              <Link className={classes.moreLessLink} component='button' onClick={handleShowLess}>
                <Box
                  sx={{
                    ml: 2,
                  }}
                >
                  <Typography>
                    <Trans>Свернуть</Trans>
                  </Typography>
                </Box>
              </Link>
            )}
          </Box>
        )}
        <SecondarySmallButton onClick={handleConfigure}>
          <Typography className={classes.sensorSettingsText}>
            <Trans>Настроить чёрные списки</Trans>
          </Typography>
        </SecondarySmallButton>
      </Box>
    );
  };

  const renderCorrectWords = () => {
    if (sensorName !== 'as-typo') return null;

    const handleConfigure = () => {
      navigate('/home/main/correct-words', {
        state: {background: location},
      });
    };
    const wordsCount = t('plurals.words', {count: correctWords.length});

    return (
      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          mt: 4,
        }}
      >
        {correctWords && (
          <Box
            sx={{
              mb: 1,
            }}
          >
            <Typography
              className={classes.sensorSettingsText}
              sx={{
                display: 'inline',
              }}
            >
              <Trans>Ваш словарь: {{wordsCount}}</Trans>
            </Typography>
          </Box>
        )}
        <SecondarySmallButton onClick={handleConfigure}>
          <Typography className={classes.sensorSettingsText}>
            <Trans>Настроить свой словарь</Trans>
          </Typography>
        </SecondarySmallButton>
      </Box>
    );
  };

  const renderGeoGroup = (geoGroup: GeoGroupType) => {
    const handleRemove = async () => {
      const newGeoGroups = geoGroups
        .filter(
          v => v.name !== geoGroup.name || JSON.stringify(v.geos) != JSON.stringify(geoGroup.geos),
        )
        .map(v => ({
          name: v.name,
          geos: v.geos.map(vv => ({
            id: vv.id,
            name: vv.name,
            parentName: vv.parentName,
          })),
        }));
      await saveGeoGroups({variables: {geoGroups: newGeoGroups}});
      await refetch();
    };

    return (
      <>
        <Grid item xs={11} sm={4}>
          {geoGroup.name}
        </Grid>
        <Grid item xs={11} sm={7}>
          <Hidden mdDown>
            <Typography>{geoGroup.geos.map(v => v.name).join(', ')}</Typography>
          </Hidden>
          <Hidden smUp>
            <Typography className={classes.secondaryText}>
              {geoGroup.geos.map(v => v.name).join(', ')}
            </Typography>
          </Hidden>
        </Grid>
        <Grid item xs={1} sm={1}>
          <IconButton className={classes.removeIcon} onClick={handleRemove} size='large'>
            <img alt='Remove' height={18} src={removeImage} width={18} />
          </IconButton>
        </Grid>
      </>
    );
  };

  const renderGeoNameMismatch = () => {
    if (sensorName !== 'as-geo-name-mismatch') return null;

    const handleAdd = () => {
      navigate(
        {
          pathname: '/home/main/geo-groups',
          search: `?${searchParams.toString()}`,
        },
        {
          state: {background: location},
        },
      );
    };

    return (
      <>
        {geoGroups.length == 0 && (
          <Typography className={classes.secondaryText}>
            <Trans>Вы можете настроить более точное срабатывание для данного сенсора.</Trans>
          </Typography>
        )}
        {geoGroups.length > 0 && (
          <Grid container spacing={3}>
            <Hidden mdDown>
              <Grid item xs={4}>
                <Typography className={classes.secondaryText}>
                  <Trans>Фраза</Trans>
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography className={classes.secondaryText}>
                  <Trans>Связанные регионы</Trans>
                </Typography>
              </Grid>
            </Hidden>
            {geoGroups.map(v => renderGeoGroup(v))}
          </Grid>
        )}
        <Box
          sx={{
            mt: 8,
          }}
        >
          <PrimaryMediumButton onClick={handleAdd}>
            <Trans>Добавить</Trans>
          </PrimaryMediumButton>
        </Box>
      </>
    );
  };

  const renderStratLowConv = () => {
    if (sensorName !== 'as-strat-low-conv') return null;

    const handleSuccess = () => {
      setIsSnackbarOpen(true);
    };

    return (
      <StratLowConvSettings
        periodDays={stratLowConvState.periodDays}
        minConversions={stratLowConvState.minConversions}
        onSuccess={handleSuccess}
      />
    );
  };

  const renderStopWords = () => {
    if (sensorName !== 'as-ad-stop-words') return null;

    const sortedStopWords = showAllStopWords
      ? stopWords.toSorted()
      : stopWords.toSorted().slice(0, MAX_STOP_WORDS);

    const moreCount = stopWords.length - MAX_STOP_WORDS;
    const showMoreLink = !showAllStopWords && moreCount > 0;
    const showLessLink = showAllStopWords && moreCount > MAX_STOP_WORDS;

    const handleShowMore = () => setShowAllStopWords(true);
    const handleShowLess = () => setShowAllStopWords(false);

    const handleClickConfigure = () => {
      navigate('/home/main/stop-words', {
        state: {background: location},
      });
    };

    const stopWordsText = sortedStopWords.join(', ');
    return (
      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          mt: 4,
        }}
      >
        {sortedStopWords.length > 0 && (
          <Box
            sx={{
              mb: 1,
            }}
          >
            <Typography
              className={classes.sensorSettingsText}
              sx={{
                display: 'inline',
              }}
            >
              <Trans>Ваш список стоп-слов: {{stopWordsText}}</Trans>
            </Typography>

            {showMoreLink && (
              <Link className={classes.moreLessLink} component='button' onClick={handleShowMore}>
                <Box
                  sx={{
                    ml: 2,
                  }}
                >
                  <Typography>
                    <Trans>Ещё {{moreCount}}</Trans>
                  </Typography>
                </Box>
              </Link>
            )}

            {showLessLink && (
              <Link className={classes.moreLessLink} component='button' onClick={handleShowLess}>
                <Box
                  sx={{
                    ml: 2,
                  }}
                >
                  <Typography>
                    <Trans>Свернуть</Trans>
                  </Typography>
                </Box>
              </Link>
            )}
          </Box>
        )}
        <SecondarySmallButton onClick={handleClickConfigure}>
          <Typography className={classes.sensorSettingsText}>
            <Trans>Настроить стоп-слова</Trans>
          </Typography>
        </SecondarySmallButton>
      </Box>
    );
  };

  const renderAdDoubleSettings = () => {
    if (sensorName !== 'as-ad-double') return null;

    const handleClickCheckbox = () => {
      setIsModineDouble(!isMobileDouble);
    };

    const handleSave = async () => {
      await saveAdDoubleSettings({variables: {settings: {isMobileDouble}}});
    };

    return (
      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          mt: 4,
        }}
      >
        <Box
          onClick={handleClickCheckbox}
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Checkbox className={classes.checkbox} checked={isMobileDouble} />
          <Typography>Считать дублями тексты мобильных объявлений</Typography>
        </Box>
        <PrimaryMediumButton disabled={saveAdDoubleSettingsLoading} onClick={handleSave}>
          <Trans>Сохранить</Trans>
        </PrimaryMediumButton>
      </Box>
    );
  };

  const renderUtmCoherencySettings = () => {
    if (sensorName !== 'as-utm-coherency') return null;

    const handleSave = async () => {
      const skipParams = skipParamsUtmCoherency.filter(v => v.length > 0);
      await saveUtmCoherencySettings({variables: {settings: {skipParams}}});
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const lines = event.target.value.toLowerCase().split('\n');
      setSkipParamsUtmCoherency(lines);
    };

    const handleClickChip = (label: string) => {
      const existingElement = skipParamsUtmCoherency.find(v => v === label);
      if (!existingElement) {
        const currentFilledValues = skipParamsUtmCoherency.filter(v => v.length > 0);
        setSkipParamsUtmCoherency([...currentFilledValues, label]);
      }
    };

    const inputValue = skipParamsUtmCoherency.join('\n');

    return (
      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          mt: 4,
        }}
      >
        <Box
          sx={{
            width: '100%',
            mb: 1,
          }}
        >
          <Box
            sx={{
              mb: 4,
            }}
          >
            <Typography>Исключить UTM-метки из проверки</Typography>
          </Box>
          <MediumTextField
            placeholder={t('Добавьте UTM-метку', 'Добавьте UTM-метку')}
            value={inputValue}
            myProps={{placeholderFocused: ''}}
            multiline
            minRows={10}
            data-testid='textarea-as-utm-coherency'
            onChange={handleChange}
          />
        </Box>
        <Box
          sx={{
            mt: 1,
            mb: 4,
            display: 'flex',
            gap: 8,
          }}
        >
          {UTMS.map((s, i) => (
            <Chip
              key={i}
              label={s}
              size='small'
              variant='outlined'
              data-testid={`chip-${s}`}
              onClick={() => handleClickChip(s)}
            />
          ))}
        </Box>
        <PrimaryMediumButton
          disabled={saveUtmCoherencySettingsLoading}
          data-testid='save-as-utm-coherency'
          onClick={handleSave}
        >
          <Trans>Сохранить</Trans>
        </PrimaryMediumButton>
      </Box>
    );
  };

  const renderKpiPeriodSettings = () => {
    if (!KPI_SENSORS.includes(sensorName!)) return null;

    const handlePeriodChange = (event: ChangeEvent<HTMLInputElement>) => {
      setKpiPeriodDays(Number(event.target.value));
    };

    const handleSave = async () => {
      const settings = {periodDays: kpiPeriodDays};

      try {
        if (sensorName === 'as-kpi-age-gender') {
          await saveKpiAgeGenderSettings({variables: {settings}});
        } else if (sensorName === 'as-kpi-place') {
          await saveKpiPlaceSettings({variables: {settings}});
        } else if (sensorName === 'as-kpi-geo') {
          await saveKpiGeoSettings({variables: {settings}});
        }
        setIsSnackbarOpen(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving KPI settings:', error);
      }
    };

    const isLoading =
      saveKpiAgeGenderSettingsLoading || saveKpiPlaceSettingsLoading || saveKpiGeoSettingsLoading;

    return (
      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          mt: 4,
        }}
      >
        <Box sx={{mb: 2}}>
          <Typography className={classes.subTitle}>
            <Trans>Период проверки</Trans>
          </Typography>
        </Box>
        <Box sx={{mb: 4}}>
          <Typography className={classes.secondaryText}>
            <Trans>Укажите период проверки в днях</Trans>
          </Typography>
        </Box>
        <Box sx={{mb: 4}}>
          <TextField
            type='number'
            value={kpiPeriodDays}
            onChange={handlePeriodChange}
            inputProps={{
              min: 1,
              'data-testid': `kpi-period-input-${sensorName}`,
            }}
          />
        </Box>
        <PrimaryMediumButton
          disabled={isLoading}
          onClick={handleSave}
          data-testid={`save-kpi-period-${sensorName}`}
        >
          <Trans>Сохранить</Trans>
        </PrimaryMediumButton>
      </Box>
    );
  };

  const renderMediaAdAnomalyGrowCpmSettings = () => {
    if (sensorName !== 'as-media-ad-anomaly-grow-cpm') return null;

    const handlePeriodChange = (event: ChangeEvent<HTMLInputElement>) => {
      setMediaAdAnomalyCpmSettings(prev => ({...prev, periodDays: Number(event.target.value)}));
    };

    const handleMultiplierChange = (event: ChangeEvent<HTMLInputElement>) => {
      setMediaAdAnomalyCpmSettings(prev => ({
        ...prev,
        comparisonMultiplierPercent: Number(event.target.value),
      }));
    };

    const handleMinAnalysisChange = (event: ChangeEvent<HTMLInputElement>) => {
      setMediaAdAnomalyCpmSettings(prev => ({
        ...prev,
        minAnalysisImpressions: Number(event.target.value),
      }));
    };

    const handleMinComparisonChange = (event: ChangeEvent<HTMLInputElement>) => {
      setMediaAdAnomalyCpmSettings(prev => ({
        ...prev,
        minComparisonImpressions: Number(event.target.value),
      }));
    };

    const handleSave = async () => {
      try {
        await saveMediaAdAnomalyCpmSettings({
          variables: {
            settings: mediaAdAnomalyCpmSettings,
          },
        });
        setIsSnackbarOpen(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving media ad anomaly CPM settings:', error);
      }
    };

    return (
      <Box sx={{alignItems: 'flex-start', display: 'flex', flexDirection: 'column', mt: 4}}>
        <Box sx={{mb: 2}}>
          <Typography className={classes.subTitle}>
            <Trans>Период анализа в днях</Trans>
          </Typography>
        </Box>
        <Box sx={{mb: 4}}>
          <TextField
            type='number'
            value={mediaAdAnomalyCpmSettings.periodDays}
            onChange={handlePeriodChange}
            inputProps={{
              min: 0,
              'data-testid': 'media-ad-anomaly-cpm-period-input',
            }}
          />
        </Box>
        <Box sx={{mb: 2}}>
          <Typography className={classes.subTitle}>
            <Trans>Порог превышения, %</Trans>
          </Typography>
        </Box>
        <Box sx={{mb: 4}}>
          <TextField
            type='number'
            value={mediaAdAnomalyCpmSettings.comparisonMultiplierPercent}
            onChange={handleMultiplierChange}
            inputProps={{
              min: 0,
              'data-testid': 'media-ad-anomaly-cpm-multiplier-input',
            }}
          />
        </Box>
        <Box sx={{mb: 2}}>
          <Typography className={classes.subTitle}>
            <Trans>Мин. показов за период анализа</Trans>
          </Typography>
        </Box>
        <Box sx={{mb: 4}}>
          <TextField
            type='number'
            value={mediaAdAnomalyCpmSettings.minAnalysisImpressions}
            onChange={handleMinAnalysisChange}
            inputProps={{
              min: 0,
              'data-testid': 'media-ad-anomaly-cpm-min-analysis-input',
            }}
          />
        </Box>
        <Box sx={{mb: 2}}>
          <Typography className={classes.subTitle}>
            <Trans>Мин. показов за период сравнения</Trans>
          </Typography>
        </Box>
        <Box sx={{mb: 4}}>
          <TextField
            type='number'
            value={mediaAdAnomalyCpmSettings.minComparisonImpressions}
            onChange={handleMinComparisonChange}
            inputProps={{
              min: 0,
              'data-testid': 'media-ad-anomaly-cpm-min-comparison-input',
            }}
          />
        </Box>
        <PrimaryMediumButton
          disabled={saveMediaAdAnomalyCpmSettingsLoading}
          onClick={handleSave}
          data-testid='save-media-ad-anomaly-cpm-settings'
        >
          <Trans>Сохранить</Trans>
        </PrimaryMediumButton>
      </Box>
    );
  };

  const renderCpmPlanExceededSettings = () => {
    if (sensorName !== 'as-cpm-plan-exceeded') return null;

    const handlePeriodChange = (event: ChangeEvent<HTMLInputElement>) => {
      setCpmPlanExceededSettings(prev => ({...prev, periodDays: Number(event.target.value)}));
    };

    const handleMinImpressionsChange = (event: ChangeEvent<HTMLInputElement>) => {
      setCpmPlanExceededSettings(prev => ({...prev, minImpressions: Number(event.target.value)}));
    };

    const handlePlanCpmChange = (planCpm: CpmPlanItem[]) => {
      setCpmPlanExceededSettings(prev => ({...prev, planCpm}));
    };

    const handleSave = async () => {
      try {
        const filteredPlanCpm = cpmPlanExceededSettings.planCpm.filter(
          item => item.campaignId.trim() !== '',
        );
        await saveCpmPlanExceededSettings({
          variables: {
            settings: {
              periodDays: cpmPlanExceededSettings.periodDays,
              planCpm: filteredPlanCpm.map(({campaignId, cpm}) => ({campaignId, cpm})),
              minImpressions: cpmPlanExceededSettings.minImpressions,
            },
          },
        });
        setIsSnackbarOpen(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving CPM plan exceeded settings:', error);
      }
    };

    return (
      <Box sx={{alignItems: 'flex-start', display: 'flex', flexDirection: 'column', mt: 4}}>
        <Box sx={{mb: 2}}>
          <Typography className={classes.subTitle}>
            <Trans>Период анализа в днях</Trans>
          </Typography>
        </Box>
        <Box sx={{mb: 4}}>
          <TextField
            type='number'
            value={cpmPlanExceededSettings.periodDays}
            onChange={handlePeriodChange}
            inputProps={{
              min: 0,
              'data-testid': 'cpm-plan-exceeded-period-input',
            }}
          />
        </Box>
        <Box sx={{mb: 2}}>
          <Typography className={classes.subTitle}>
            <Trans>Мин. показов для срабатывания</Trans>
          </Typography>
        </Box>
        <Box sx={{mb: 4}}>
          <TextField
            type='number'
            value={cpmPlanExceededSettings.minImpressions}
            onChange={handleMinImpressionsChange}
            inputProps={{
              min: 0,
              'data-testid': 'cpm-plan-exceeded-min-impressions-input',
            }}
          />
        </Box>
        <Box sx={{mb: 4, width: '100%'}}>
          <CpmPlanEditor value={cpmPlanExceededSettings.planCpm} onChange={handlePlanCpmChange} />
        </Box>
        <PrimaryMediumButton
          disabled={saveCpmPlanExceededSettingsLoading}
          onClick={handleSave}
          data-testid='save-cpm-plan-exceeded-settings'
        >
          <Trans>Сохранить</Trans>
        </PrimaryMediumButton>
      </Box>
    );
  };

  const renderNewAnomalyFallSettings = () => {
    if (!NEW_ANOMALY_FALL_SENSORS.includes(sensorName!)) return null;

    const isVtr = sensorName === 'as-campaign-anomaly-fall-vtr';
    const isVisitDuration = sensorName === 'as-ad-anomaly-fall-visit-duration';
    const isPageDepth = sensorName === 'as-ad-anomaly-fall-page-depth';

    const currentSettings = isVtr
      ? campaignFallVtrSettings
      : isVisitDuration
        ? adFallVisitDurationSettings
        : adFallPageDepthSettings;

    const handlePeriodChange = (event: ChangeEvent<HTMLInputElement>) => {
      const value = Number(event.target.value);
      if (isVtr) setCampaignFallVtrSettings(prev => ({...prev, periodDays: value}));
      if (isVisitDuration) setAdFallVisitDurationSettings(prev => ({...prev, periodDays: value}));
      if (isPageDepth) setAdFallPageDepthSettings(prev => ({...prev, periodDays: value}));
    };

    const handleMultiplierChange = (event: ChangeEvent<HTMLInputElement>) => {
      const value = Number(event.target.value);
      if (isVtr) {
        setCampaignFallVtrSettings(prev => ({...prev, comparisonMultiplierPercent: value}));
      }
      if (isVisitDuration) {
        setAdFallVisitDurationSettings(prev => ({...prev, comparisonMultiplierPercent: value}));
      }
      if (isPageDepth) {
        setAdFallPageDepthSettings(prev => ({...prev, comparisonMultiplierPercent: value}));
      }
    };

    const handleAttributionChange = (value: YalperAnalyticAttributionModelEnum) => {
      if (isVisitDuration) {
        setAdFallVisitDurationSettings(prev => ({...prev, attributionModel: value}));
      }
      if (isPageDepth) {
        setAdFallPageDepthSettings(prev => ({...prev, attributionModel: value}));
      }
    };

    const handleMinVisitsChange = (event: ChangeEvent<HTMLInputElement>) => {
      setAdFallPageDepthSettings(prev => ({...prev, minVisits: Number(event.target.value)}));
    };

    const handleSave = async () => {
      try {
        if (isVtr) {
          await saveCampaignFallVtrSettings({
            variables: {settings: campaignFallVtrSettings},
          });
        }
        if (isVisitDuration) {
          await saveAdFallVisitDurationSettings({
            variables: {settings: adFallVisitDurationSettings},
          });
        }
        if (isPageDepth) {
          await saveAdFallPageDepthSettings({
            variables: {settings: adFallPageDepthSettings},
          });
        }
        setIsSnackbarOpen(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving anomaly fall settings:', error);
      }
    };

    const isLoading =
      saveCampaignFallVtrLoading || saveAdFallVisitDurationLoading || saveAdFallPageDepthLoading;

    return (
      <Box sx={{alignItems: 'flex-start', display: 'flex', flexDirection: 'column', mt: 4}}>
        <Box sx={{mb: 2}}>
          <Typography className={classes.subTitle}>
            <Trans>Период анализа в днях</Trans>
          </Typography>
        </Box>
        <Box sx={{mb: 4}}>
          <TextField
            type='number'
            value={currentSettings.periodDays}
            onChange={handlePeriodChange}
            inputProps={{
              min: 0,
              'data-testid': `anomaly-fall-period-input-${sensorName}`,
            }}
          />
        </Box>
        <Box sx={{mb: 2}}>
          <Typography className={classes.subTitle}>
            <Trans>Порог падения, %</Trans>
          </Typography>
        </Box>
        <Box sx={{mb: 2}}>
          <TextField
            type='number'
            value={currentSettings.comparisonMultiplierPercent}
            onChange={handleMultiplierChange}
            inputProps={{
              min: 0,
              max: 100,
              'data-testid': `anomaly-fall-multiplier-input-${sensorName}`,
            }}
          />
        </Box>
        <Box sx={{mb: 4}}>
          <Typography className={classes.secondaryText}>
            <Trans>Значение в % падения. 50 = упало в 2 раза, 90 = упало в 10 раз</Trans>
          </Typography>
        </Box>
        {!isVtr && (
          <>
            <Box sx={{mb: 2}}>
              <Typography className={classes.subTitle}>
                <Trans>Модель атрибуции</Trans>
              </Typography>
            </Box>
            <Box sx={{mb: 4}}>
              <FormControl>
                <Select
                  value={(currentSettings as typeof adFallVisitDurationSettings).attributionModel}
                  onChange={e =>
                    handleAttributionChange(e.target.value as YalperAnalyticAttributionModelEnum)
                  }
                  data-testid={`anomaly-fall-attribution-select-${sensorName}`}
                >
                  {ATTRIBUTION_MODELS.map(model => (
                    <MenuItem key={model.value} value={model.value}>
                      {model.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </>
        )}
        {isPageDepth && (
          <>
            <Box sx={{mb: 2}}>
              <Typography className={classes.subTitle}>
                <Trans>Мин. количество визитов</Trans>
              </Typography>
            </Box>
            <Box sx={{mb: 4}}>
              <TextField
                type='number'
                value={(currentSettings as typeof adFallPageDepthSettings).minVisits}
                onChange={handleMinVisitsChange}
                inputProps={{
                  min: 0,
                  'data-testid': `anomaly-fall-min-visits-input-${sensorName}`,
                }}
              />
            </Box>
          </>
        )}
        <PrimaryMediumButton
          disabled={isLoading}
          onClick={handleSave}
          data-testid={`save-anomaly-fall-settings-${sensorName}`}
        >
          <Trans>Сохранить</Trans>
        </PrimaryMediumButton>
      </Box>
    );
  };

  const renderExceptions = () => {
    const fields = R.pipe(
      R.filter<ExceptionFieldsetType>(v => platforms.includes(v.platform)),
      R.map(v => v.fields),
      R.flatten,
      R.uniq,
    )(exceptionFieldsets);

    const items = fields.map(v => ({id: v, text: t(fieldName(v))}));

    const rows = R.pipe(R.groupBy((v: ExceptionType) => R.pipe(R.values, R.join('|'))(v.items[0])))(
      exceptions,
    );

    const handleSelect = (id: string) => {
      setSelectedField(id);
      setShowFilterModal(true);
    };

    const handleClose = () => {
      setShowFilterModal(false);
    };

    const handleSave = async (field: string, operator: Operator, value: string) => {
      const selectedPlatforms = R.pipe(
        R.map((v: ExceptionFieldsetType) => v.platform),
        R.uniq,
        R.filter((v: YalperPlatform) => platforms.includes(v)),
      )(exceptionFieldsets);

      await Promise.all(
        selectedPlatforms.map(async v => {
          await createException({
            variables: {
              companyId,
              sensorName,
              platform: v,
              items: [{field, operator, value}],
            },
          });
        }),
      );
      mixpanel.track('click_save_exception', {
        sensorName,
        platforms: selectedPlatforms,
      });
      refetch();
      setShowFilterModal(false);
    };

    const handleRemove = async (ids: string[]) => {
      await Promise.all(
        ids.map(async id => {
          await deleteException({variables: {id}});
        }),
      );
      refetch();
    };

    return (
      <>
        {exceptions.length == 0 && (
          <Typography className={classes.secondaryText}>
            <Trans>
              С помощью функционала исключений вы можете исключить срабатывание сенсора по
              определенным параметрам.
              <br />
              <br />
              Например, если вы хотите, чтобы сенсор не учитывал кампании с расходом менее 5000
              руб., нужно выбрать “Расход”, а затем “Меньше” и указать нужное вам значение.
              <br />
              <br />
              По каждому сенсору вы можете видеть уникальный набор атрибутов, свойственный именно
              ему. По каким-то сенсорам выводится возможность выбрать количество кликов, по каким-то
              – название цели, а для других – расход или название группы.
              <br />
              <br />
              Мы рекомендуем настроить исключения для всех сенсоров, по которым вы видите
              неактуальные для вас срабатывания, чтобы сделать отчет максимально полезным.
            </Trans>
          </Typography>
        )}
        {exceptions.length > 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <Grid container spacing={2}>
                <Grid item xs={4} sm={4}>
                  <Typography className={classes.secondaryText}>
                    <Trans>Свойство</Trans>
                  </Typography>
                </Grid>
                <Grid item xs={3} sm={3}>
                  <Typography className={classes.secondaryText}>
                    <Trans>Условие</Trans>
                  </Typography>
                </Grid>
                <Grid item xs={4} sm={4}>
                  <Typography className={classes.secondaryText}>
                    <Trans>Значение</Trans>
                  </Typography>
                </Grid>
                <Grid item xs={1} sm={1} />
              </Grid>
            </Grid>

            {Object.keys(rows).map((key, i) => {
              const row = rows[key];
              if (row) {
                const item = row[0]?.items[0];
                const ids = row.map(v => v.id);

                if (ids) {
                  return (
                    <Grid item xs={12} sm={12} key={i}>
                      <Grid container spacing={2}>
                        <Grid item xs={4} sm={4}>
                          <Typography>{t(fieldName(item.field))}</Typography>
                        </Grid>
                        <Grid item xs={3} sm={3}>
                          <Typography>{t(`operators.${item.operator}`)}</Typography>
                        </Grid>
                        <Grid item xs={4} sm={4}>
                          <Typography className={classes.exceptionValue}>{item.value}</Typography>
                        </Grid>
                        <Grid item xs={1} sm={1}>
                          <IconButton
                            className={classes.removeIcon}
                            onClick={() => handleRemove(ids)}
                          >
                            <img alt='Remove filter' height={18} src={removeImage} width={18} />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                } else {
                  return <></>;
                }
              }
            })}
          </Grid>
        )}
        <Box
          sx={{
            mt: 15,
          }}
        >
          <PrimaryMenu text={t('Добавить условие')} items={items} onSelect={handleSelect} />
        </Box>
        {showFilterModal && (
          <Modal header={t('Исключать по условию')} onClose={handleClose}>
            <ExceptionForm field={selectedField} onSave={handleSave} />
          </Modal>
        )}
      </>
    );
  };

  const renderUtmFormatSettings = () => {
    if (sensorName !== 'as-utm-format' && sensorName !== 'as-utm-format-appsflyer') {
      return null;
    }

    const handleInputChange = (index: number, event: any) => {
      const newInputs = [...utmFormatValues];
      newInputs[index] = event.target.value;

      setUtmFormatValues(newInputs);
    };

    const handleKeyDown = (index: number, event: any) => {
      if (event.key === 'Enter') {
        event.preventDefault();

        if (index === utmFormatValues.length - 1 && event.target.value.length) {
          setUtmFormatValues([...utmFormatValues, '']);
        }
      }
    };

    const handleRemoveInput = (value: string) => {
      if (utmFormatValues.length === 1) {
        setUtmFormatValues(['']);
      } else {
        let valueRemoved = false;

        const newUtmFormatValues = utmFormatValues.filter(input => {
          if (input === value && !valueRemoved) {
            valueRemoved = true;

            return false;
          }

          return true;
        });

        setUtmFormatValues(newUtmFormatValues);
      }
    };

    const handleSave = async () => {
      const formattedValues = utmFormatValues
        .map(value => value.trim())
        .filter(value => value.length);

      if (sensorName === 'as-utm-format') {
        await saveUtmFormatSettings({
          variables: {
            settings: {
              filterSources: formattedValues,
              minVisitsForOldProblems: utmFormatMinVisitsForOldErrors,
              minVisits: utmFormatMinVisits,
            },
          },
        }).then(() => setIsSnackbarOpen(true));
      }

      if (sensorName === 'as-utm-format-appsflyer') {
        await saveUtmFormatWebdavSettings({
          variables: {settings: {filterSources: formattedValues}},
        }).then(() => setIsSnackbarOpen(true));
      }
    };

    const handleMinVisitsChange = (
      target: TMinVisits,
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      if (target === 'minVisits') {
        setUtmFormatMinVisits(Number(event.target.value));
      }

      if (target === 'minVisitsForOldErrors') {
        setUtmFormatMinVisitsForOldErrors(Number(event.target.value));
      }
    };

    return (
      <Box sx={{mt: 4}}>
        <Box sx={{mb: 4}}>
          <Typography className={classes.subTitle}>
            <Trans>Фильтры</Trans>
          </Typography>
        </Box>

        <Box sx={{mb: 4}}>
          {sensorName === 'as-utm-format' ? (
            <Typography className={classes.secondaryText}>
              <Trans>
                Укажите значение параметра utm_source, которые необходимо оставить в отчете.
              </Trans>
              <br />
              <Trans>Добавляйте по принципу «Одна метка — один фильтр».</Trans>
              <br />
              <Trans>Количество фильтров не ограничено.</Trans>
              <br />
              <Trans>Для добавления нового поля нажмите клавишу Enter</Trans>
            </Typography>
          ) : (
            <Typography className={classes.secondaryText}>
              <Trans>
                Укажите значение параметра Media source, которые необходимо оставить в отчете.
              </Trans>
              <br />
              <Trans>Добавляйте по принципу «Одна метка — один фильтр».</Trans>
              <br />
              <Trans>Количество фильтров не ограничено.</Trans>
              <br />
              <Trans>Для добавления нового поля нажмите клавишу Enter</Trans>
            </Typography>
          )}
        </Box>

        <Box sx={{mb: 4}}>
          {utmFormatValues.map((value, index) => (
            <TextField
              key={index}
              onChange={event => handleInputChange(index, event)}
              onKeyDown={event => handleKeyDown(index, event)}
              className={classes.multyInput}
              value={value ? value : ''}
              inputProps={{
                'data-testid': `as-utm-format-input-${index}`,
                autoFocus: true,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <Button
                      className={classes.removeButton}
                      onClick={() => handleRemoveInput(value)}
                      data-testid={`as-utm-format-input-${index}-remove`}
                    >
                      <CancelOutlinedIcon className={classes.removeButtonIcon} />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          ))}
        </Box>

        <Box sx={{mb: 4}} data-testid='as-utm-format-submit-result'>
          <Trans>Ваши метки</Trans>: {utmFormatValues.join(', ')}
        </Box>

        {sensorName === 'as-utm-format' && (
          <>
            <MinVisitsFIeld
              type='minVisitsForOldErrors'
              value={utmFormatMinVisitsForOldErrors}
              dataTestId='as-utm-format-min-visit-for-old-errors-input'
              onChange={handleMinVisitsChange}
            />
            <MinVisitsFIeld
              type='minVisits'
              value={utmFormatMinVisits}
              dataTestId='as-utm-format-min-visit-input'
              onChange={handleMinVisitsChange}
            />
          </>
        )}

        <PrimaryMediumButton
          disabled={saveUtmFormatSettingsLoading || saveUtmFormatWebdavSettingsLoading}
          data-testid='as-utm-format-submit-button'
          onClick={handleSave}
        >
          <Trans>Сохранить</Trans>
        </PrimaryMediumButton>
      </Box>
    );
  };

  const handleChangeTab = (value: string) => {
    setActiveTab(value);
  };

  const tabs = () => {
    const tabs = TABS.map(v => ({...v, text: t(v.text)}));
    if (hasSettings()) {
      return tabs;
    }
    return tabs.filter(v => v.name !== SETTINGS_TAB);
  };

  return (
    <Root>
      <Box>
        <BackHeader />
      </Box>
      <Box
        sx={{
          mt: 8,
        }}
      >
        <Title>
          <Trans>Настройки сенсора</Trans>
        </Title>
      </Box>
      <Box
        sx={{
          mt: 4,
          mb: 4,
        }}
      >
        <Typography className={classes.secondaryText}>{humanName}</Typography>
      </Box>
      <NativeTabs
        activeTabName={activeTab}
        tabs={tabs()}
        variant='DARK'
        onChange={handleChangeTab}
      />
      {!isClientAccess && activeTab == SETTINGS_TAB && (
        <Box
          sx={{
            mt: 8,
          }}
        >
          {renderRegions()}
          {renderBadWords()}
          {renderCorrectWords()}
          {renderGeoNameMismatch()}
          {renderStratLowConv()}
          {renderStopWords()}
          {renderAdDoubleSettings()}
          {renderUtmCoherencySettings()}
          {renderUtmFormatSettings()}
          {renderKpiPeriodSettings()}
          {renderMediaAdAnomalyGrowCpmSettings()}
          {renderCpmPlanExceededSettings()}
          {renderNewAnomalyFallSettings()}
        </Box>
      )}
      {!isClientAccess && activeTab == FILTERS_TAB && (
        <Box
          sx={{
            mt: 8,
          }}
        >
          {renderExceptions()}
        </Box>
      )}

      <Snackbar
        isSnackbarOpen={isSnackbarOpen}
        handleSnackbarClose={handleSnackbarClose}
        message={t('Сохранено')}
      />
    </Root>
  );
};
